import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MouseEvent } from 'react';
import { TopologyViewStreamFrameFromJSON } from '@reap/openapi-public-v1';

import TopologyGraphRenderer from '@app/components/topology/TopologyGraphRenderer';
import {
  applyTopologyOpsToGraph,
  extractTopologySnapshot,
  resolveTopologyFrameType
} from '@app/pages/org/topology/topologyStream';
import { ChatActiveArtifactContextschemaSurfaceEnum } from '@app/types/sdk';
import type {
  ChatActiveArtifactContextschema,
  ChatRunEventArtifactUpdateschema,
  TopologyArtifactOverlayStatusDataschema,
  TopologyGraphOverlayschema,
  TopologyV2Drilldownschema,
  TopologyV2Edgeschema,
  TopologyV2Nodeschema,
  TopologyV2ResolveResponse,
  TopologyV2ResolveRequestPatchschema,
  TopologyViewStreamFrame,
  TopologyViewStreamPatchData
} from '@app/types/sdk';
import {
  applyTopologyArtifactUpdate,
  applyTopologyOverlayStatus,
  deriveTopologyOverlayPresentation,
  hydrateTopologyOverlayState,
  setTopologyLayerLocalVisibility,
  topologyOverlaySnapshot,
  type TopologyArtifactOverlaySnapshot
} from '@app/utils/topologyOverlay';
import {
  buildTopologyOverlayBase,
  buildTopologyResolveRequest,
  topologyArtifactKey,
  type TopologyArtifactContent,
  type TopologyResolveRequest
} from '@app/utils/topologyArtifact';
import { topologyLayerStatusLabel, topologyLayerStatusTitle } from '@app/utils/topologyLayerStatus';
import {
  formatTopologyPathLayerMetadata,
  formatTopologyPathRelatedViewBreadcrumb,
  formatTopologyRelationshipLayerMetadata
} from '@app/pages/org/topology/topologyWorkbench';

type TopologyArtifactSurface = NonNullable<ChatActiveArtifactContextschema['surface']>;

type Props = {
  topology: TopologyArtifactContent;
  artifactId?: string;
  surface?: TopologyArtifactSurface;
  artifactUpdates?: ChatRunEventArtifactUpdateschema[];
  selectedNodeIds?: Set<string>;
  selectedEdgeIds?: Set<string>;
  onActivate?: (context: {
    artifactId: string;
    topology: TopologyArtifactContent;
    surface: TopologyArtifactSurface;
    overlay: TopologyArtifactOverlaySnapshot;
  }) => void;
  onOverlayStateChange?: (artifactId: string, snapshot: TopologyArtifactOverlaySnapshot) => void;
  onNodeSelect?: (event: MouseEvent, nodeId: string) => void;
  onEdgeSelect?: (event: MouseEvent, edgeId: string) => void;
  onPaneClick?: (event: MouseEvent) => void;
  autoActivate?: boolean;
  resolveTopologyView: (viewId: string, request: TopologyResolveRequest) => Promise<TopologyV2ResolveResponse>;
  streamTopologyView?: (
    viewId: string,
    request: TopologyResolveRequest,
    onEvent: (type: string, data: string) => void,
    signal: AbortSignal,
    fromSeq?: number
  ) => Promise<void>;
  streamTopologyArtifact?: (
    artifactId: string,
    overlayRevision: number,
    onEvent: (type: string, data: string) => void,
    signal: AbortSignal,
    fromSeq?: number
  ) => Promise<void>;
  compact?: boolean;
  className?: string;
};

const extractSeq = (resolved: TopologyV2ResolveResponse): number => {
  const candidates = [
    resolved.meta?.lastEventId,
    resolved.meta?.lastSeq,
    resolved.graph.meta?.lastEventId,
    resolved.graph.meta?.lastSeq
  ];
  for (const value of candidates) {
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
      return Math.floor(value);
    }
  }
  return 0;
};

const pluralize = (count: number, singular: string, plural: string): string => `${count} ${count === 1 ? singular : plural}`;

const asRecord = (value: unknown): Record<string, unknown> | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }
  return value as Record<string, unknown>;
};

const hasLiveRuntimeLayer = (layer: any): boolean => {
  const runtime = asRecord(layer?.runtime);
  return runtime?.deliveryMode === 'live' && layer?.visible !== false;
};

const liveRuntimeLayerKey = (layers: any[]): string =>
  layers
    .filter(hasLiveRuntimeLayer)
    .map((layer) => String(layer.id ?? '').trim())
    .filter(Boolean)
    .sort()
    .join(',');

const formatTimeLabel = (topology: TopologyArtifactContent): string => {
  const binding = topology.binding;
  if (binding.timeMode === 'current') {
    return 'Live';
  }
  const asOf = binding.asOf instanceof Date ? binding.asOf : binding.asOf ? new Date(binding.asOf) : null;
  return asOf && !Number.isNaN(asOf.getTime()) ? `As of ${asOf.toLocaleString()}` : 'Fixed';
};

type TopologyNode = TopologyV2Nodeschema;
type TopologyEdge = TopologyV2Edgeschema;
type TopologyDrilldown = TopologyV2Drilldownschema;
type TopologyResolvePatch = TopologyV2ResolveRequestPatchschema;

const nodeKindFromRefs = (node?: TopologyNode): string | undefined => {
  for (const ref of node?.refs ?? []) {
    const refType = typeof ref?.type === 'string' ? ref.type.trim().toLowerCase() : '';
    if (refType.startsWith('aws.')) {
      return refType.slice('aws.'.length).trim() || undefined;
    }
  }
  return undefined;
};

const nodeLabelValue = (node: TopologyNode | undefined, labelKey: string): unknown => {
  const key = labelKey.trim();
  if (!key) {
    return undefined;
  }
  if (node?.labels?.[key] !== undefined) {
    return node.labels[key];
  }
  return key === 'kind' ? nodeKindFromRefs(node) : undefined;
};

const getDeep = (obj: any, path: string[]): unknown => {
  let cur: any = obj;
  for (const part of path) {
    if (!cur || typeof cur !== 'object') {
      return undefined;
    }
    cur = cur[part];
  }
  return cur;
};

const evaluateDrilldownWhen = (
  when: Record<string, unknown> | undefined,
  contextMeta: Record<string, unknown>,
  node?: TopologyNode,
  edge?: TopologyEdge
): boolean => {
  if (!when || Object.keys(when).length === 0) {
    return true;
  }
  const getValue = (key: string): unknown => {
    const k = key.trim();
    if (k === 'node.id') return node?.id;
    if (k === 'edge.id') return edge?.id;
    if (k === 'edge.fromId') return edge?.fromId;
    if (k === 'edge.toId') return edge?.toId;
    if (k.startsWith('labels.')) {
      const labelKey = k.slice('labels.'.length);
      return node ? nodeLabelValue(node, labelKey) : edge ? edge.labels?.[labelKey] : undefined;
    }
    if (k.startsWith('node.labels.')) return nodeLabelValue(node, k.slice('node.labels.'.length));
    if (k.startsWith('edge.labels.')) return edge?.labels?.[k.slice('edge.labels.'.length)];
    if (k.startsWith('context.')) return contextMeta?.[k.slice('context.'.length)];
    return undefined;
  };

  return Object.entries(when).every(([key, expected]) => {
    const actual = getValue(key);
    if (Array.isArray(expected)) {
      return expected.some((item) => String(item) === String(actual ?? ''));
    }
    return String(expected) === String(actual ?? '');
  });
};

const evalDrilldownExpression = (
  value: unknown,
  env: { request: Record<string, unknown>; context: Record<string, unknown>; node?: TopologyNode; edge?: TopologyEdge }
): unknown => {
  if (typeof value !== 'string') {
    return value;
  }
  const resolveToken = (token: string): unknown => {
    const expr = token.trim();
    if (!expr) return undefined;
    if (expr === 'node.id') return env.node?.id;
    if (expr === 'edge.id') return env.edge?.id;
    if (expr === 'edge.fromId') return env.edge?.fromId;
    if (expr === 'edge.toId') return env.edge?.toId;
    if (expr.startsWith('node.labels.')) return nodeLabelValue(env.node, expr.slice('node.labels.'.length));
    if (expr.startsWith('edge.labels.')) return env.edge?.labels?.[expr.slice('edge.labels.'.length)];
    if (expr.startsWith('node.properties.')) return getDeep(env.node?.properties ?? {}, expr.slice('node.properties.'.length).split('.'));
    if (expr.startsWith('edge.properties.')) return getDeep(env.edge?.properties ?? {}, expr.slice('edge.properties.'.length).split('.'));
    if (expr.startsWith('request.')) return getDeep(env.request, expr.slice('request.'.length).split('.'));
    if (expr.startsWith('context.')) return getDeep(env.context, expr.slice('context.'.length).split('.'));
    return undefined;
  };

  const trimmed = value.trim();
  const exactTemplate = trimmed.match(/^\$\{([^}]+)\}$/);
  if (exactTemplate) {
    return resolveToken(exactTemplate[1] ?? '') ?? '';
  }
  if (trimmed.includes('${')) {
    return trimmed.replace(/\$\{([^}]+)\}/g, (_match, tokenExpr: string) => {
      const resolved = resolveToken(tokenExpr);
      return resolved === undefined || resolved === null ? '' : String(resolved);
    });
  }
  return resolveToken(trimmed) ?? value;
};

const evalDrilldownDeep = (
  value: unknown,
  env: { request: Record<string, unknown>; context: Record<string, unknown>; node?: TopologyNode; edge?: TopologyEdge }
): unknown => {
  const evaluated = evalDrilldownExpression(value, env);
  if (Array.isArray(evaluated)) {
    return evaluated.map((item) => evalDrilldownDeep(item, env));
  }
  const rec = asRecord(evaluated);
  if (!rec) {
    return evaluated;
  }
  return Object.fromEntries(Object.entries(rec).map(([key, item]) => [key, evalDrilldownDeep(item, env)]));
};

const applyDrilldownPatch = (
  baseRequest: Record<string, unknown>,
  patch: TopologyResolvePatch | undefined,
  env: { request: Record<string, unknown>; context: Record<string, unknown>; node?: TopologyNode; edge?: TopologyEdge }
): Record<string, unknown> => {
  if (!patch) {
    return baseRequest;
  }
  const next: Record<string, unknown> = { ...baseRequest };
  const scopeType = evalDrilldownDeep(patch.scopeType, env);
  const scopeId = evalDrilldownDeep(patch.scopeId, env);
  const asOf = evalDrilldownDeep(patch.asOf, env);
  const overlayKey = evalDrilldownDeep(patch.overlayKey, env);
  if (typeof scopeType === 'string' && scopeType) next.scopeType = scopeType;
  if (typeof scopeId === 'string' && scopeId) next.scopeId = scopeId;
  if (typeof asOf === 'string' && asOf) next.asOf = asOf;
  if (typeof overlayKey === 'string' && overlayKey) next.overlayKey = overlayKey;

  const params = evalDrilldownDeep(patch.params, env);
  if (params && typeof params === 'object' && !Array.isArray(params)) {
    next.params = { ...(asRecord(baseRequest.params) ?? {}), ...(params as Record<string, unknown>) };
  }
  const limits = evalDrilldownDeep(patch.limits, env);
  if (limits && typeof limits === 'object' && !Array.isArray(limits)) {
    next.limits = { ...(asRecord(baseRequest.limits) ?? {}), ...(limits as Record<string, unknown>) };
  }
  return next;
};

const findFirstOverlayDrilldown = (
  drilldowns: TopologyDrilldown[],
  contextMeta: Record<string, unknown>,
  node?: TopologyNode,
  edge?: TopologyEdge
): TopologyDrilldown | null => {
  for (const drilldown of drilldowns) {
    if (!drilldown?.name || !drilldown?.viewId) {
      continue;
    }
    if (drilldown.mode && drilldown.mode !== 'overlay') {
      continue;
    }
    if (evaluateDrilldownWhen(drilldown.appliesWhen, contextMeta, node, edge)) {
      return drilldown;
    }
  }
  return null;
};

const sanitizeOverlayPart = (value: string): string => value.trim().replace(/[^a-zA-Z0-9_.:-]+/g, '-').replace(/^-+|-+$/g, '');

const drilldownGraphOverlayId = (viewId: string, overlayKey: string): string =>
  ['chat-ui', 'drilldown', sanitizeOverlayPart(viewId), sanitizeOverlayPart(overlayKey)].filter(Boolean).join(':');

const responseContextMeta = (response: TopologyV2ResolveResponse | null | undefined): Record<string, unknown> =>
  asRecord(response?.meta?.context) ?? {};

const graphOverlayDrilldowns = (overlay: TopologyGraphOverlayschema): TopologyDrilldown[] => {
  const raw = asRecord(overlay.meta)?.drilldowns;
  return Array.isArray(raw) ? (raw as TopologyDrilldown[]) : [];
};

export const TopologyArtifactView = ({
  topology,
  artifactId,
  surface = ChatActiveArtifactContextschemaSurfaceEnum.ChatCard,
  artifactUpdates = [],
  selectedNodeIds,
  selectedEdgeIds,
  onActivate,
  onOverlayStateChange,
  onNodeSelect,
  onEdgeSelect,
  onPaneClick,
  autoActivate,
  resolveTopologyView,
  streamTopologyView,
  streamTopologyArtifact,
  compact,
  className
}: Props) => {
  const lastSeqRef = useRef(0);
  const artifactKey = useMemo(() => topologyArtifactKey(topology), [topology]);
  const topologyValidation = useMemo(() => {
    try {
      return {
        overlayBase: buildTopologyOverlayBase(topology),
        error: null as string | null
      };
    } catch (err) {
      return {
        overlayBase: undefined,
        error: err instanceof Error ? err.message : 'Invalid topology artifact.'
      };
    }
  }, [artifactKey]);
  const overlayBase = topologyValidation.overlayBase;
  const [resolved, setResolved] = useState<TopologyV2ResolveResponse | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);
  const [streamStatus, setStreamStatus] = useState<'idle' | 'streaming' | 'closed' | 'error'>('idle');
  const [layersOpen, setLayersOpen] = useState(false);
  const [overlayState, setOverlayState] = useState(() =>
    hydrateTopologyOverlayState(topology.presentation?.overlayState, overlayBase)
  );

  useEffect(() => {
    let cancelled = false;
    lastSeqRef.current = 0;
    setResolved(null);
    setStreamStatus('idle');
    setLayersOpen(false);
    setOverlayState(hydrateTopologyOverlayState(topology.presentation?.overlayState, overlayBase));

    if (topologyValidation.error) {
      setError(topologyValidation.error);
      setStatus('error');
      return;
    }

    setStatus('loading');
    setError(null);

    (async () => {
      try {
        const request = buildTopologyResolveRequest(topology);
        const response = await resolveTopologyView(topology.binding.viewId, request);
        if (cancelled) {
          return;
        }
        lastSeqRef.current = extractSeq(response);
        setResolved(response);
        setStatus('ready');
      } catch (err) {
        if (cancelled) {
          return;
        }
        setError(err instanceof Error ? err.message : 'Failed to resolve topology');
        setStatus('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [artifactKey, overlayBase, resolveTopologyView, topologyValidation.error]);

  const liveLayerKey = useMemo(() => liveRuntimeLayerKey(overlayState.layers as any[]), [overlayState.layers]);

  useEffect(() => {
    const useArtifactStream = Boolean(artifactId && streamTopologyArtifact && liveLayerKey);
    const useViewStream = !useArtifactStream && topology.binding.timeMode === 'current' && Boolean(streamTopologyView);
    if (status !== 'ready' || (!useArtifactStream && !useViewStream)) {
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    let request: TopologyResolveRequest;
    try {
      request = buildTopologyResolveRequest(topology);
    } catch {
      return;
    }

    const handleEvent = (eventType: string, data: string) => {
      if (cancelled || controller.signal.aborted) {
        return;
      }
      let rawFrame: Record<string, unknown> | null = null;
      let frame: TopologyViewStreamFrame | null = null;
      try {
        rawFrame = JSON.parse(data) as Record<string, unknown>;
        frame = TopologyViewStreamFrameFromJSON(rawFrame);
      } catch {
        return;
      }
      if (frame.viewId && frame.viewId !== topology.binding.viewId) {
        return;
      }
      if (frame.scopeType && frame.scopeType !== request.scopeType) {
        return;
      }
      if (frame.scopeId && frame.scopeId !== request.scopeId) {
        return;
      }
      if (typeof frame.seq === 'number' && Number.isFinite(frame.seq)) {
        const seq = Math.floor(frame.seq);
        lastSeqRef.current = Math.max(lastSeqRef.current, seq);
      }

      const frameType = resolveTopologyFrameType(eventType, frame.type);
      const rawPayload =
        rawFrame && rawFrame.data && typeof rawFrame.data === 'object' && !Array.isArray(rawFrame.data)
          ? (rawFrame.data as Record<string, unknown>)
          : undefined;
      const payload =
        frame.data && typeof frame.data === 'object' && !Array.isArray(frame.data)
          ? (frame.data as unknown as Record<string, unknown>)
          : rawPayload ?? {};
      if (frameType === 'overlay_status') {
        if (!useArtifactStream) {
          return;
        }
        const statusData = payload as unknown as TopologyArtifactOverlayStatusDataschema;
        if (artifactId && statusData.artifactId && statusData.artifactId !== artifactId) {
          return;
        }
        setOverlayState((prev) => applyTopologyOverlayStatus(prev, statusData));
        return;
      }
      if (frameType === 'snapshot') {
        setResolved((prev) => extractTopologySnapshot(payload, prev) ?? prev);
        return;
      }
      if (frameType === 'patch') {
        const ops = (payload as unknown as TopologyViewStreamPatchData).ops;
        if (!Array.isArray(ops) || ops.length === 0) {
          return;
        }
        setResolved((prev) => {
          if (!prev?.graph) {
            return prev;
          }
          return { ...prev, graph: applyTopologyOpsToGraph(prev.graph, ops) };
        });
      }
    };

    setStreamStatus('streaming');
    const streamPromise =
      useArtifactStream && artifactId
        ? streamTopologyArtifact!(artifactId, overlayState.revision, handleEvent, controller.signal)
        : streamTopologyView!(topology.binding.viewId, request, handleEvent, controller.signal, lastSeqRef.current);

    void streamPromise
      .then(() => {
        if (!cancelled && !controller.signal.aborted) {
          setStreamStatus('closed');
        }
      })
      .catch(() => {
        if (!cancelled && !controller.signal.aborted) {
          setStreamStatus('error');
        }
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [
    artifactId,
    artifactKey,
    liveLayerKey,
    overlayState.revision,
    status,
    streamTopologyArtifact,
    streamTopologyView,
    topology
  ]);

  useEffect(() => {
    if (!artifactId || artifactUpdates.length === 0 || topologyValidation.error) {
      return;
    }
    setOverlayState((previous) =>
      artifactUpdates.reduce((next, update) => applyTopologyArtifactUpdate(next, update, topology), previous)
    );
  }, [artifactId, artifactUpdates, topology, topologyValidation.error]);

  const overlaySnapshot = useMemo(() => topologyOverlaySnapshot(overlayState), [overlayState]);

  useEffect(() => {
    if (!artifactId || !onOverlayStateChange) {
      return;
    }
    onOverlayStateChange(artifactId, overlaySnapshot);
  }, [artifactId, onOverlayStateChange, overlaySnapshot]);

  const activate = useCallback(() => {
    if (!artifactId || !onActivate) {
      return;
    }
    onActivate({ artifactId, topology, surface, overlay: overlaySnapshot });
  }, [artifactId, onActivate, overlaySnapshot, surface, topology]);

  useEffect(() => {
    if (autoActivate) {
      activate();
    }
  }, [activate, autoActivate]);

  const handleNodeSelect = useCallback(
    (event: MouseEvent, nodeId: string) => {
      activate();
      onNodeSelect?.(event, nodeId);
    },
    [activate, onNodeSelect]
  );

  const handleEdgeSelect = useCallback(
    (event: MouseEvent, edgeId: string) => {
      activate();
      onEdgeSelect?.(event, edgeId);
    },
    [activate, onEdgeSelect]
  );

  const handlePaneClick = useCallback(
    (event: MouseEvent) => {
      activate();
      onPaneClick?.(event);
    },
    [activate, onPaneClick]
  );

  const resolveArtifactDrilldown = useCallback(
    async (event: MouseEvent, kind: 'node' | 'edge', id: string) => {
      event.preventDefault();
      activate();
      if (!resolved?.graph) {
        return;
      }

      let ownerRequest: Record<string, unknown>;
      try {
        ownerRequest = buildTopologyResolveRequest(topology) as unknown as Record<string, unknown>;
      } catch {
        return;
      }

      let ownerContext = responseContextMeta(resolved);
      let ownerDrilldowns = (resolved.drilldowns ?? []) as TopologyDrilldown[];
      let node = kind === 'node' ? resolved.graph.nodes?.find((item) => item.id === id) : undefined;
      let edge = kind === 'edge' ? resolved.graph.edges?.find((item) => item.id === id) : undefined;

      for (const overlay of [...overlayState.graphOverlays].reverse()) {
        const overlayNode = kind === 'node' ? overlay.graph.nodes?.find((item: TopologyNode) => item.id === id) : undefined;
        const overlayEdge = kind === 'edge' ? overlay.graph.edges?.find((item: TopologyEdge) => item.id === id) : undefined;
        if (!overlayNode && !overlayEdge) {
          continue;
        }
        const meta = asRecord(overlay.meta);
        ownerRequest = asRecord(meta?.request) ?? ownerRequest;
        ownerContext = asRecord(meta?.context) ?? ownerContext;
        ownerDrilldowns = graphOverlayDrilldowns(overlay);
        node = overlayNode;
        edge = overlayEdge;
        break;
      }

      const drilldown = findFirstOverlayDrilldown(ownerDrilldowns, ownerContext, node, edge);
      if (!drilldown) {
        return;
      }

      const env = { request: ownerRequest, context: ownerContext, node, edge };
      const withStatic = applyDrilldownPatch(ownerRequest, drilldown._static, env);
      const nextRequest = applyDrilldownPatch(withStatic, drilldown.dynamic, env);
      let overlayKey = typeof nextRequest.overlayKey === 'string' ? nextRequest.overlayKey.trim() : '';
      if (!overlayKey && edge?.id) {
        overlayKey = `${drilldown.viewId}:edge:${edge.id}`;
      }
      if (!overlayKey && node?.id) {
        overlayKey = `${drilldown.viewId}:node:${node.id}`;
      }
      if (!overlayKey) {
        return;
      }

      const overlayId = drilldownGraphOverlayId(drilldown.viewId, overlayKey);
      if (overlayState.graphOverlays.some((overlay) => overlay.id === overlayId)) {
        setOverlayState((current) => ({
          ...current,
          graphOverlays: current.graphOverlays.filter((overlay) => overlay.id !== overlayId)
        }));
        return;
      }

      const { overlayKey: _overlayKey, ...wireRequest } = nextRequest as Record<string, unknown> & { overlayKey?: unknown };
      try {
        const response = await resolveTopologyView(drilldown.viewId, wireRequest as unknown as TopologyResolveRequest);
        const graphOverlay: TopologyGraphOverlayschema = {
          id: overlayId,
          label: drilldown.name,
          sourceViewId: topology.binding.viewId,
          viewId: drilldown.viewId,
          overlayKey,
          graph: response.graph,
          meta: {
            source: 'chat-ui',
            request: wireRequest,
            context: responseContextMeta(response),
            drilldowns: response.drilldowns ?? [],
            target: id
          }
        };
        setOverlayState((current) => ({
          ...current,
          graphOverlays: [...current.graphOverlays.filter((overlay) => overlay.id !== graphOverlay.id), graphOverlay]
        }));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to resolve topology drilldown');
      }
    },
    [activate, overlayState.graphOverlays, resolveTopologyView, resolved, topology]
  );

  const handleNodeDoubleClick = useCallback(
    (event: MouseEvent, nodeId: string) => {
      void resolveArtifactDrilldown(event, 'node', nodeId);
    },
    [resolveArtifactDrilldown]
  );

  const handleEdgeDoubleClick = useCallback(
    (event: MouseEvent, edgeId: string) => {
      void resolveArtifactDrilldown(event, 'edge', edgeId);
    },
    [resolveArtifactDrilldown]
  );

  const binding = topology.binding;
  const renderedPresentation = useMemo(
    () => (resolved?.graph ? deriveTopologyOverlayPresentation(resolved.graph, overlayState) : null),
    [overlayState, resolved?.graph]
  );
  const renderedGraph = renderedPresentation?.graph ?? null;
  const activeRelatedView = renderedPresentation?.relatedView;
  const nodeCount = renderedGraph?.nodes.length ?? 0;
  const edgeCount = renderedGraph?.edges.length ?? 0;
  const layerCount = overlayState.layers.length;
  const localHiddenLayerIds = new Set(overlayState.localHiddenLayerIds);
  const graphOverlayKey = overlayState.graphOverlays.map((overlay) => overlay.id).sort().join(',');
  const effectiveSelectedNodeIds = useMemo<Set<string>>(
    () => new Set<string>([...Array.from(overlayState.selection.nodeIds ?? []), ...Array.from(selectedNodeIds ?? [])] as string[]),
    [overlayState.selection.nodeIds, selectedNodeIds]
  );
  const effectiveSelectedEdgeIds = useMemo<Set<string>>(
    () => new Set<string>([...Array.from(overlayState.selection.edgeIds ?? []), ...Array.from(selectedEdgeIds ?? [])] as string[]),
    [overlayState.selection.edgeIds, selectedEdgeIds]
  );
  const classes = ['chat-artifact-topology', compact ? 'chat-artifact-topology--compact' : '', className ?? '']
    .filter(Boolean)
    .join(' ');

  if (status === 'loading') {
    return (
      <div className={classes}>
        <p className="chat-artifact-topology__status">Resolving topology...</p>
      </div>
    );
  }

  if (status === 'error' || !renderedGraph) {
    return (
      <div className={classes}>
        <p className="chat-artifact-topology__error">{error ?? 'Failed to render topology.'}</p>
      </div>
    );
  }

  return (
    <div className={classes} onFocus={activate} onMouseEnter={activate}>
      {(topology.title || layerCount > 0) && (
        <div className="chat-artifact-topology__header">
          {topology.title && <div className="chat-artifact-topology__title">{topology.title}</div>}
          {layerCount > 0 && (
            <div className="chat-artifact-topology__layers">
              <button
                type="button"
                className="chat-artifact-topology__layers-toggle"
                aria-expanded={layersOpen}
                onClick={() => setLayersOpen((open) => !open)}>
                Layers {layerCount}
              </button>
              {layersOpen && (
                <div className="chat-artifact-topology__layers-menu">
                  {overlayState.needsReload && (
                    <div className="chat-artifact-topology__layers-warning">Layer history is incomplete; refresh artifact if needed.</div>
                  )}
                  {overlayState.layers.map((layer) => {
                    const visible = layer.visible !== false && !localHiddenLayerIds.has(layer.id);
                    const layerStatus = overlayState.layerStatuses[layer.id];
                    const statusLabel = topologyLayerStatusLabel(layerStatus?.status);
                    const relationshipSummary = formatTopologyRelationshipLayerMetadata(layer);
                    const pathSummary = formatTopologyPathLayerMetadata(layer);
                    const relatedViewBreadcrumb = formatTopologyPathRelatedViewBreadcrumb(layer);
                    return (
                      <div key={layer.id} className="chat-artifact-topology__layer-entry">
                        <div className="chat-artifact-topology__layer-row">
                          <span className="chat-artifact-topology__layer-label" title={layer.label}>
                            {layer.label}
                          </span>
                          <span className="chat-artifact-topology__layer-kind">{layer.kind}</span>
                          <span
                            className={`chat-artifact-topology__layer-status chat-artifact-topology__layer-status--${statusLabel}`}
                            title={topologyLayerStatusTitle(layerStatus)}
                          >
                            {statusLabel}
                          </span>
                          <button
                            type="button"
                            className="chat-artifact-topology__layer-action"
                            aria-pressed={visible}
                            onClick={() => setOverlayState((current) => setTopologyLayerLocalVisibility(current, layer.id, !visible))}>
                            {visible ? 'Hide' : 'Show'}
                          </button>
                        </div>
                        {relationshipSummary && (
                          <div className="chat-artifact-topology__layer-relationship" title={relationshipSummary}>
                            {relationshipSummary}
                          </div>
                        )}
                        {pathSummary && (
                          <div className="chat-artifact-topology__layer-relationship" title={pathSummary}>
                            {pathSummary}
                          </div>
                        )}
                        {relatedViewBreadcrumb && (
                          <div className="chat-artifact-topology__layer-breadcrumb" title={relatedViewBreadcrumb}>
                            {relatedViewBreadcrumb}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {activeRelatedView && (
        <div className="chat-artifact-topology__related-view">
          <span>Path detail</span>
          <strong>{activeRelatedView.label}</strong>
          {activeRelatedView.sourceViewId && <em>from {activeRelatedView.sourceViewId}</em>}
        </div>
      )}
      <div className="chat-artifact-topology__canvas">
        <TopologyGraphRenderer
          topology={renderedGraph}
          graphKey={`${artifactKey}:${renderedGraph.graphId}:${activeRelatedView?.layerId ?? 'base'}:${lastSeqRef.current}:${overlayState.revision}:${overlayState.localHiddenLayerIds.join(',')}:${graphOverlayKey}`}
          selectedNodeIds={effectiveSelectedNodeIds}
          selectedEdgeIds={effectiveSelectedEdgeIds}
          onNodeSelect={handleNodeSelect}
          onEdgeSelect={handleEdgeSelect}
          onNodeDoubleClick={handleNodeDoubleClick}
          onEdgeDoubleClick={handleEdgeDoubleClick}
          onPaneClick={handlePaneClick}
          showControls={!compact}
        />
      </div>
      <div className="chat-artifact-topology__meta">
        <span>{binding.viewId}</span>
        <span>
          {binding.scope.scopeType}:{binding.scope.scopeId}
        </span>
        <span>
          {pluralize(nodeCount, 'node', 'nodes')} / {pluralize(edgeCount, 'edge', 'edges')}
        </span>
        <span>{formatTimeLabel(topology)}</span>
        {binding.timeMode === 'current' && <span>{streamStatus === 'streaming' ? 'Streaming' : streamStatus}</span>}
      </div>
    </div>
  );
};
